/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, NgZone, OnChanges, OnInit, SimpleChanges, signal} from '@angular/core';
import * as _ from 'lodash';

import template from './inactive-targets.component.html';
import {AdnInactiveTargetsService, InactiveTargetsData} from '../../../components/util/adn-inactive-targets.service';
import {StateService} from '@uirouter/core';
import {APP_SHARED} from '../app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED],
  selector: 'adn-inactive-targets',
  template
})
export class InactiveTargetsComponent implements OnInit, OnChanges {
  @Input() refresh: any;
  @Input() limit: any[];
  @Input() singular: boolean | string;
  @Input() parentObject: string;
  @Input() parentId: string;

  private readonly scanCheckState = signal(false);
  private readonly showFullDetailsState = signal(false);
  private readonly creativesState = signal<any[]>([]);
  private readonly lineItemsState = signal<any[]>([]);

  get scanCheck(): boolean { return this.scanCheckState(); }
  set scanCheck(value: boolean) { this.scanCheckState.set(value); }
  get showFullDetails(): boolean { return this.showFullDetailsState(); }
  set showFullDetails(value: boolean) { this.showFullDetailsState.set(value); }
  get creatives(): any[] { return this.creativesState(); }
  set creatives(value: any[]) { this.creativesState.set(value); }
  get lineItems(): any[] { return this.lineItemsState(); }
  set lineItems(value: any[]) { this.lineItemsState.set(value); }

  constructor(
    private readonly adnInactiveTargets: AdnInactiveTargetsService,
    private readonly $state: StateService,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.loadInactiveTargets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const inputKeys = ['parentObject', 'parentId', 'limit', 'singular', 'refresh'];
    if (inputKeys.some((key) => changes[key] && !changes[key].firstChange)) {
      this.loadInactiveTargets();
    }
  }

  get showInactiveTargets(): boolean {
    return !this.parentObject || this.parentObject === 'Order';
  }

  get isSingular(): boolean {
    return this.singular === true || this.singular === 'true';
  }

  lineItemHref(id: string): string {
    return this.$state.href('app.adv.line-items.line-item', {id});
  }

  creativeHref(id: string): string {
    return this.$state.href('app.adv.creatives.creative', {id});
  }

  private loadInactiveTargets(): void {
    if (!this.showInactiveTargets) {
      this.finishLoading([], []);
      return;
    }

    this.scanCheck = false;
    const limitIds = _.map(this.limit || [], 'id');
    const isOrder = this.parentObject === 'Order';

    this.adnInactiveTargets.results(this.refresh).then(
      (data: InactiveTargetsData) => {
        this.ngZone.run(() => {
          const creatives = _.filter(data?.creatives || [], (c: any) => {
            return this.isSingular ? limitIds.indexOf(_.get(c, ['creative', 'id'])) > -1 : c;
          });
          const lineItems = _.filter(data?.lineItems || [], (li: any) => {
            if (isOrder) {
              return String(_.get(li, ['order', 'id'])) === String(this.parentId);
            }
            return this.isSingular ? limitIds.indexOf(_.get(li, ['lineItem', 'id'])) > -1 : li;
          });
          this.finishLoading(creatives, lineItems);
        });
      },
      () => {
        this.ngZone.run(() => {
          this.finishLoading([], []);
        });
      }
    );
  }

  private finishLoading(creatives: any[], lineItems: any[]): void {
    this.creatives = creatives;
    this.lineItems = lineItems;
    this.scanCheck = true;
  }
}
